import { ElementRef, Injectable } from '@angular/core';

import {
  BehaviorSubject,
  forkJoin,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  throttleTime,
} from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { DateTimeService, IPageData, IParty, PrintService } from '@tablesready/common-ui';
import { AuthService } from '@tablesready/frontend-auth';
import { ClockService } from '@tablesready/frontend-alerts-bar';
import { PartiesService } from '@tablesready/frontend-api-services';
import { HeaderTitleService } from '@tablesready/frontend-page-badge';
import { FrontendPartyObserverService } from './frontend-party-socket-observer';

@Injectable({ providedIn: 'root' })
export class FrontendReservationsObserverService {
  infiniteScrollCallback?: () => void;
  tableOfReservations?: ElementRef;
  /** Runs once the print has been handed off (the table is copied by then). */
  afterPrint?: () => void;
  timezone: string;
  reservationDateFilter: NgbDateStruct | string = '';
  reservations_search_query = '';
  sortBy = 'quoted_time';
  sortReverse = false;
  limit = 40;
  page = 1;
  reservations: IParty[] = [];
  resCount = 0;
  disableScroll: boolean;
  loading: boolean;
  debounceMs = 200;
  isPrinting = false;
  now;

  private readonly subscription = new Subscription();

  private readonly reservationsData = new BehaviorSubject<IPageData>({
    page: this.page,
    sortBy: this.sortBy,
    sortReverse: this.sortReverse,
    limit: this.limit,
    search: this.reservations_search_query,
    date: '',
  });

  constructor(
    private readonly partyEventService: FrontendPartyObserverService,
    private readonly dateTimeService: DateTimeService,
    private readonly clockService: ClockService,
    private readonly partiesService: PartiesService,
    private readonly printService: PrintService,
    private readonly authService: AuthService,
    private readonly headerService: HeaderTitleService,
  ) {
    this.subscription.add(this.clockService.clock$.subscribe(now => this.now = now));

    const dataSub = this.reservationsData.asObservable()
      .pipe(
        // Check debounceMs
        throttleTime(this.debounceMs),
        switchMap(options => forkJoin([this.updateReservations(options), of(options)])),
        tap(([data, options]) => {
          const { parties, count } = data;

          if (this.infiniteScrollCallback) {
            this.infiniteScrollCallback();
          }

          if (!parties.length && options.page !== 1) {
            this.disableScroll = true;
            this.loading = false;

            return false;
          }
          this.updateReservation(cloneDeep(parties), options.page);
          this.loading = false;
          this.updatePartiesCount(count);
          this.page = options.page + options.limit / this.limit;

          if (this.isPrinting) {
            // SetTimeout for waiting while DOM is updated
            setTimeout(() => {
              if (this.tableOfReservations) {
                this.printService.print(this.tableOfReservations);
              }
              this.afterPrint?.();
              this.afterPrint = undefined;
            }, 0);
            this.isPrinting = false;
          }
        }),
      )
      .subscribe();
    this.subscription.add(dataSub);

    const socketsSub = this.partyEventService.partyUpdateEvent$
      .pipe(
        throttleTime(this.debounceMs),
        tap(() => {
          const statusPage = Math.ceil(this.reservations.length / this.limit) || 1;
          this.reservationsEventHandler(statusPage);
        }),
      )
      .subscribe();
    this.subscription.add(socketsSub);
  }

  reservationsEventHandler(statusPage = 1): void {
    this.page = 1;
    if (this.loading) {
      return;
    }

    this.disableScroll = false;
    this.emitUpdateReservationsEvent(1, statusPage);
  }

  updateReservation(reservations, page = 1): void {
    if (page === 1) {
      this.reservations = reservations;
    } else {
      this.reservations = this.partiesService.unique([...this.reservations, ...reservations]);
    }
    // This.updatePartiesFilter();
  }

  updateReservations(options): Observable<any> {
    this.loading = true;
    const { page, limit, sortBy, sortReverse, search, type, date } = options;
    const sort = {};
    sort[sortBy] = sortReverse ? -1 : 1;

    return this.partiesService.getReservations({
      page,
      limit,
      search,
      sort,
      type,
      date,
    });
  }

  updatePartiesCount(partiesCount: number): void {
    this.resCount = partiesCount;
    this.headerService.setDataForRoute('reservations', { badgeCount: partiesCount });
  }

  getAllReservations() {
    const date = this.dateFilterToTimestamp();

    const initOptions = {
      sortBy: this.sortBy,
      sortReverse: this.sortReverse,
      search: this.reservations_search_query,
      date,
    };

    this.isPrinting = true;
    this.reservationsData.next(initOptions);
  }

  loadMore() {
    if (this.loading) {
      return false;
    }
    this.loading = true;
    this.emitUpdateReservationsEvent(this.page);
  }

  updateSearchQuery(query: string): void {
    this.reservations_search_query = query;
    this.page = 1;
    this.emitUpdateReservationsEvent();
  }

  showCheckInButton(party: IParty): boolean {
    return this.partiesService.showCheckInButton(party, this.now);
  }

  changeDate(): void {
    if (!this.reservationDateFilter) {
      this.reservationDateFilter = '';
    }

    this.emitUpdateReservationsEvent();
  }

  deleteDate() {
    this.reservationDateFilter = '';
    this.emitUpdateReservationsEvent();
  }

  emitUpdateReservationsEvent(page = 1, statusPage = 1): void {
    const isAuthenticated = this.authService.isAuthenticated();

    if (isAuthenticated) {
      const date = this.dateFilterToTimestamp();

      const initOptions = {
        page,
        sortBy: this.sortBy,
        sortReverse: this.sortReverse,
        limit: this.limit * statusPage,
        search: this.reservations_search_query,
        date,
      };

      this.reservationsData.next(initOptions);
    }
  }

  // Anything that isn't a real NgbDateStruct (empty string, raw datepicker text)
  // is treated as "no date filter" so an invalid date can never reach the API
  private dateFilterToTimestamp(): number | '' {
    return this.dateTimeService.isValidNgbDate(this.reservationDateFilter)
      ? this.dateTimeService.ngbDateToMoment(this.reservationDateFilter, this.timezone).valueOf()
      : '';
  }
}
